import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { deriveHiringActivityEvents } from '../utils/hiringActivity';
import { getRecentUpdates } from './firebase/updates';
import { getRecentCoursesAndLessons, getRecentCourseUploads } from './firebase/courses';
import { fetchHRTemplates } from '../utils/hrTemplates';
import { fetchTemplateStructure } from './hrStructureService';
import { listAllJobTreadImports } from './templatesImportsService';
import { listClaudeSkills } from './claudeSkillsService';
import { loadOrgChartData } from './orgChartService';
import { fetchGroupEmojis } from './groupEmojiService';
import { JOBTREAD_IMPORT_TYPES } from './technologyCommunityService';

// Emoji + tile styling for each feed item kind. Items carry their own emoji when
// they have one (e.g. a template's picked emoji); otherwise the kind's default
// emoji here is shown — every row renders an emoji, never a lucide icon.
export const WHATS_NEW_KIND_META = {
  update: {
    emoji: '📣',
    label: 'Update',
    tile: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
  },
  course: {
    emoji: '🎓',
    label: 'New Course',
    tile: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
  },
  lesson: {
    emoji: '📖',
    label: 'New Lesson',
    tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
  },
  document: {
    emoji: '📄',
    label: 'New Document',
    tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  },
  version: {
    emoji: '🆕',
    label: 'New Version',
    tile: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
  },
  automation: {
    emoji: '⚡',
    label: 'New Automation',
    tile: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
  },
  import: {
    emoji: '📥',
    label: 'New Import',
    tile: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
  },
  skill: {
    emoji: '✨',
    label: 'New Skill',
    tile: 'bg-fuchsia-50 dark:bg-fuchsia-900/30 text-fuchsia-600 dark:text-fuchsia-400',
  },
  app: {
    emoji: '🔧',
    label: 'New Tool',
    tile: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  },
  hiring: {
    emoji: '🤝',
    label: 'Hiring Update',
    tile: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  },
  sop: {
    emoji: '📋',
    label: 'SOP',
    tile: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  },
  jobdescription: {
    emoji: '👥',
    label: 'Job Description',
    tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  },
  kpi: {
    emoji: '📈',
    label: 'KPI',
    tile: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
  },
  orgchart: {
    emoji: '🏢',
    label: 'Org Chart',
    tile: 'bg-slate-50 dark:bg-slate-900/30 text-slate-600 dark:text-slate-400',
  },
};

// Where a template-version item takes the client, by the template's pillar.
// HR, Sales, and Finance deep-link to the exact material card via ?material=<key>.
const templatePillarPath = (pillar, materialKey) => {
  const key = encodeURIComponent(materialKey || '');
  switch (String(pillar).toLowerCase()) {
    case 'hr':
      return `/portal/hr/materials${key ? `?material=${key}` : ''}`;
    case 'sales':
      return `/portal/sales/resources${key ? `?material=${key}` : ''}`;
    case 'operations':
      return '/portal/operations/sops';
    case 'technology':
      return '/portal/technology';
    case 'finance':
      return `/portal/finance/materials${key ? `?material=${key}` : ''}`;
    case 'marketing':
      return '/portal/marketing';
    default:
      return null;
  }
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Build the "What's New" feed items for a department (or the global feed when
 * `department` is null). Returns a date-sorted (newest first) array of items;
 * the same source of truth that each portal department's What's New card uses.
 *
 * Sources per scope:
 *  - global (department = null): portal updates, recent courses/lessons, new
 *    course documents, and recent template versions across pillars.
 *  - department: courses/lessons in that category + template versions from that
 *    pillar, plus department-only sources (Technology: automations / JobTread
 *    imports / Claude skills / tools (apps); HR: hiring positions & activity;
 *    Operations: SOPs / job descriptions / KPIs / org chart).
 */
export async function buildDepartmentWhatsNewItems({ department = null, currentUser, companyId, maxItems } = {}) {
  const dept = department ? String(department).toLowerCase() : null;
  const cap = maxItems ?? (dept ? 5 : 8);
  const isTech = dept === 'technology';
  const isHR = dept === 'hr';
  // Material emojis live in the pillar's editable structure (per-item), not on
  // the template collection doc — fetch it so template versions can show the
  // material's own emoji instead of a generic "new" badge.
  const structureId = ['hr', 'sales', 'finance'].includes(dept) ? dept : null;

  const [updatesRes, coursesRes, uploadsRes, templatesRes, structureRes, automationsRes, importsRes, skillsRes, positionsRes] =
    await Promise.allSettled([
      dept ? Promise.resolve([]) : getRecentUpdates(6),
      getRecentCoursesAndLessons(dept ? 30 : 6),
      dept ? Promise.resolve([]) : getRecentCourseUploads(6),
      fetchHRTemplates(),
      structureId ? fetchTemplateStructure(structureId, []) : Promise.resolve([]),
      isTech ? getDocs(collection(db, 'automations')) : Promise.resolve(null),
      isTech ? listAllJobTreadImports() : Promise.resolve([]),
      isTech ? listClaudeSkills() : Promise.resolve([]),
      isHR && currentUser?.uid
        ? getDocs(query(collection(db, 'positions'), where('userId', '==', currentUser.uid)))
        : Promise.resolve(null),
    ]);

  const matchesDept = (value) => !dept || String(value || '').toLowerCase() === dept;

  // Map material title/catalogKey → its picked emoji from the structure.
  const materialEmoji = {};
  if (structureRes.status === 'fulfilled') {
    (structureRes.value || []).forEach((section) => {
      (section.items || []).forEach((it) => {
        if (it.title && it.emoji) materialEmoji[it.title] = it.emoji;
      });
    });
  }

  const collected = [];
  if (updatesRes.status === 'fulfilled') {
    updatesRes.value
      .filter((u) => u.type !== 'Future')
      .forEach((u) => {
        const date = toDate(u.createdAt);
        if (!date) return;
        collected.push({
          id: `update-${u.id}`,
          kind: 'update',
          title: u.title,
          subtitle: u.description,
          date,
        });
      });
  } else {
    console.error("Error loading updates for What's New:", updatesRes.reason);
  }
  if (coursesRes.status === 'fulfilled') {
    coursesRes.value
      .filter((item) => matchesDept(item.category))
      .forEach((item) => {
        const date = toDate(item.sortDate);
        if (!date) return;
        collected.push({
          id: `${item.kind}-${item.courseId}-${item.lessonId || 'course'}`,
          kind: item.kind, // 'course' | 'lesson'
          title: item.title,
          subtitle: item.kind === 'lesson' ? item.courseTitle : item.category,
          date,
          path: item.path,
          // Sales courses/lessons show the book emoji rather than the
          // course/lesson kind defaults.
          emoji: dept === 'sales' ? '📖' : null,
        });
      });
  } else {
    console.error("Error loading courses for What's New:", coursesRes.reason);
  }
  if (uploadsRes.status === 'fulfilled') {
    uploadsRes.value.forEach((docItem) => {
      const date = toDate(docItem.uploadedAt);
      if (!date) return;
      collected.push({
        id: `doc-${docItem.courseId}-${docItem.lessonId}-${docItem.displayName}`,
        kind: 'document',
        title: docItem.displayName,
        subtitle: `${docItem.courseTitle} · ${docItem.lessonTitle}`,
        date,
        url: docItem.url,
      });
    });
  } else {
    console.error("Error loading documents for What's New:", uploadsRes.reason);
  }
  if (templatesRes.status === 'fulfilled') {
    templatesRes.value
      .filter((t) => t.fileUrl && (t.updatedAt || t.createdAt))
      .filter((t) => matchesDept(t.pillar || 'HR'))
      .sort((a, b) => String(b.updatedAt || b.createdAt).localeCompare(String(a.updatedAt || a.createdAt)))
      .slice(0, dept ? cap : 6)
      .forEach((t) => {
        const date = toDate(t.updatedAt || t.createdAt);
        if (!date) return;
        const pillar = t.pillar || 'HR';
        collected.push({
          id: `template-${t.id}`,
          kind: 'version',
          title: t.title || t.fileName,
          subtitle: `v${t.version || '1.0'} · ${pillar}`,
          date,
          path: templatePillarPath(pillar, t.catalogKey || t.title),
          emoji: materialEmoji[t.catalogKey || t.title] || t.emoji || null,
        });
      });
  } else {
    console.error("Error loading template versions for What's New:", templatesRes.reason);
  }

  // Technology-only sources: automations, JobTread imports, and Claude skills.
  // First uploads show as "New X"; once the version moves past 1.0 the item
  // resurfaces as "New Version" dated by its last update.
  const pushVersioned = ({ id, kind, title, typeLabel, version, createdAt, updatedAt, path, emoji }) => {
    const isNewVersion = version && String(version) !== '1.0';
    const date = toDate(isNewVersion ? updatedAt || createdAt : createdAt);
    if (!date) return;
    collected.push({
      id,
      kind: isNewVersion ? 'version' : kind,
      title,
      subtitle: isNewVersion ? `v${version} · ${typeLabel}` : typeLabel,
      date,
      path,
      emoji: emoji || null,
    });
  };
  if (automationsRes.status === 'fulfilled' && automationsRes.value) {
    automationsRes.value.docs.forEach((d) => {
      const a = d.data();
      pushVersioned({
        id: `automation-${d.id}`,
        kind: 'automation',
        title: a.name || 'Untitled Automation',
        typeLabel: 'Automation',
        version: a.version,
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
        path: `/portal/technology/automation?highlight=${d.id}`,
        emoji: a.emojis?.[0] || '🤖',
      });
    });
  } else if (automationsRes.status === 'rejected') {
    console.error("Error loading automations for What's New:", automationsRes.reason);
  }
  if (importsRes.status === 'fulfilled') {
    importsRes.value.forEach((row) => {
      const tabId = row.tabId ?? row.importType;
      const group = JOBTREAD_IMPORT_TYPES.find((t) => t.id === tabId);
      pushVersioned({
        id: `jtimport-${row.id}`,
        kind: 'import',
        title: row.title || row.fileName,
        typeLabel: group ? `${group.label} Import` : 'JobTread Import',
        version: row.version,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
        path: `/portal/technology/jobtread-imports?${tabId ? `type=${tabId}&` : ''}highlight=${row.id}`,
        emoji: row.emoji || group?.emoji,
      });
    });
  } else {
    console.error("Error loading JobTread imports for What's New:", importsRes.reason);
  }
  if (skillsRes.status === 'fulfilled') {
    skillsRes.value.forEach((row) => {
      pushVersioned({
        id: `skill-${row.id}`,
        kind: 'skill',
        title: row.title,
        typeLabel: 'Skill',
        version: row.version,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
        path: `/portal/technology/claude-skills?highlight=${row.id}`,
        emoji: row.emoji || '✨',
      });
    });
  } else {
    console.error("Error loading Claude skills for What's New:", skillsRes.reason);
  }
  // HR-only source: the client's own hiring positions. Each position seeds a
  // "Requested" event, and its linked request contributes granular activity
  // (new candidates, HR notes, favorites, status changes, timeline steps).
  if (positionsRes.status === 'fulfilled' && positionsRes.value) {
    const positionDocs = positionsRes.value.docs.map((d) => ({ id: d.id, ...d.data() }));

    positionDocs.forEach((p) => {
      const date = toDate(p.createdAt || p.updatedAt);
      if (!date) return;
      collected.push({
        id: `position-${p.id}`,
        kind: 'hiring',
        title: p.title || 'Position',
        subtitle: `Position requested · ${p.status || 'Requested'}`,
        date,
        path: `/portal/hr/hiring/position/${p.id}`,
      });
    });

    // Pull each linked position request and flatten its fulfillment activity.
    const withRequest = positionDocs.filter((p) => p.positionRequestId);
    const requestSnaps = await Promise.allSettled(
      withRequest.map((p) => getDoc(doc(db, 'positionRequests', p.positionRequestId)))
    );
    requestSnaps.forEach((res, i) => {
      if (res.status !== 'fulfilled' || !res.value.exists()) return;
      const p = withRequest[i];
      deriveHiringActivityEvents(res.value.data(), p, currentUser?.uid).forEach((event) => {
        const date = toDate(event.date);
        if (date) collected.push({ ...event, date });
      });
    });
  } else if (positionsRes.status === 'rejected') {
    console.error("Error loading hiring positions for What's New:", positionsRes.reason);
  }

  // Operations-only sources: SOP / Job Description template updates (new uploads
  // + version bumps), company KPI template updates, and org-chart adjustments.
  // Operations courses/lessons flow through the shared block above.
  if (dept === 'operations') {
    const deptLabel = (d) => (d ? d.charAt(0).toUpperCase() + d.slice(1) : 'Operations');
    const [sopEmojisRes, jdEmojisRes, sopsRes, jdsRes, kpisRes, orgRes] =
      await Promise.allSettled([
        fetchGroupEmojis('sop'),
        fetchGroupEmojis('jobDescription'),
        getDocs(collection(db, 'sopTemplates')),
        getDocs(collection(db, 'jobDescriptions')),
        getDocs(collection(db, 'kpiTemplates')),
        companyId ? loadOrgChartData(companyId) : Promise.resolve(null),
      ]);
    const sopEmojis = sopEmojisRes.status === 'fulfilled' ? sopEmojisRes.value : {};
    const jdEmojis = jdEmojisRes.status === 'fulfilled' ? jdEmojisRes.value : {};

    if (sopsRes.status === 'fulfilled') {
      sopsRes.value.docs.forEach((d) => {
        const s = d.data();
        pushVersioned({
          id: `sop-${d.id}`,
          kind: 'sop',
          title: s.title || 'SOP',
          typeLabel: deptLabel(s.department),
          version: s.version,
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          path: '/portal/operations/sops',
          emoji: s.emoji || sopEmojis[s.department] || '📋',
        });
      });
    } else {
      console.error("Error loading SOPs for What's New:", sopsRes.reason);
    }

    if (jdsRes.status === 'fulfilled') {
      jdsRes.value.docs.forEach((d) => {
        const j = d.data();
        pushVersioned({
          id: `jd-${d.id}`,
          kind: 'jobdescription',
          title: j.title || 'Job Description',
          typeLabel: deptLabel(j.department),
          version: j.version,
          createdAt: j.createdAt,
          updatedAt: j.updatedAt,
          path: '/portal/operations/job-descriptions',
          emoji: j.emoji || jdEmojis[j.department] || '📋',
        });
      });
    } else {
      console.error("Error loading job descriptions for What's New:", jdsRes.reason);
    }

    if (kpisRes.status === 'fulfilled') {
      kpisRes.value.docs.forEach((d) => {
        const k = d.data();
        const date = toDate(k.updatedAt || k.createdAt);
        if (!date) return;
        collected.push({
          id: `kpi-${d.id}`,
          kind: 'kpi',
          // The stored `name` is an auto-generated "KPI Template <date>" label,
          // not a real name — show the clean company-wide title.
          title: 'Company KPIs',
          subtitle: 'Operations',
          date,
          path: '/portal/operations/kpis',
          emoji: '📈',
        });
      });
    } else {
      console.error("Error loading KPIs for What's New:", kpisRes.reason);
    }

    if (orgRes.status === 'fulfilled' && orgRes.value) {
      const org = orgRes.value;
      const date = toDate(org.lastUpdated);
      const employees = Array.isArray(org.employees) ? org.employees : [];
      if (date && employees.length > 0) {
        collected.push({
          id: 'orgchart',
          kind: 'orgchart',
          title: 'Team Org Chart',
          subtitle: `${employees.length} member${employees.length === 1 ? '' : 's'}`,
          date,
          path: '/portal/operations/org-chart',
          emoji: '🏢',
        });
      }
    } else if (orgRes.status === 'rejected') {
      console.error("Error loading org chart for What's New:", orgRes.reason);
    }
  }

  collected.sort((a, b) => b.date - a.date);
  return collected;
}

// The six portal departments whose What's New feeds the client dashboard merges.
export const WHATS_NEW_DEPARTMENTS = ['sales', 'operations', 'technology', 'finance', 'hr', 'marketing'];

/**
 * Aggregate the most-recent What's New items across all portal departments for
 * the client dashboard. Pools each department's feed, de-dupes by id, and
 * returns the `maxItems` newest overall — but no single department may take
 * more than a third of the slots. Without that cap a department that touches
 * many docs at once (an SOP migration bumping every `updatedAt`, say) fills
 * the whole card and buries the other departments' new items.
 */
export async function buildDashboardWhatsNewItems({ currentUser, companyId, maxItems = 5 } = {}) {
  const results = await Promise.allSettled(
    WHATS_NEW_DEPARTMENTS.map((department) =>
      buildDepartmentWhatsNewItems({ department, currentUser, companyId, maxItems })
    )
  );

  const byId = new Map();
  results.forEach((res, i) => {
    if (res.status !== 'fulfilled') return;
    const department = WHATS_NEW_DEPARTMENTS[i];
    res.value.forEach((item) => {
      const existing = byId.get(item.id);
      if (!existing || item.date > existing.date) byId.set(item.id, { ...item, department });
    });
  });

  const ranked = Array.from(byId.values()).sort((a, b) => b.date - a.date);
  const perDepartment = Math.max(2, Math.ceil(maxItems / 3));
  const counts = {};
  const picked = [];
  const overflow = [];
  ranked.forEach((item) => {
    const used = counts[item.department] || 0;
    if (picked.length < maxItems && used < perDepartment) {
      counts[item.department] = used + 1;
      picked.push(item);
    } else {
      overflow.push(item);
    }
  });

  // Quiet departments leave slots unused — backfill them by recency.
  if (picked.length < maxItems) {
    picked.push(...overflow.slice(0, maxItems - picked.length));
  }

  return picked.sort((a, b) => b.date - a.date);
}
